import type { ReactNode } from 'react'

type HighlightedTextProps = {
  text: string
  /** Substring of `text` to paint with `highlightClassName`; empty string = none. */
  highlight: string
  highlightClassName: string
  /** Wraps the rest of the copy; omit to let it inherit its colour. */
  textClassName?: string
}

const wrap = (copy: string, className: string | undefined): ReactNode =>
  className === undefined ? copy : <span className={className}>{copy}</span>

/**
 * Renders copy with one phrase painted differently.
 *
 * The backend sends the phrase as a literal substring rather than markup, so
 * the split happens here on the first occurrence and every part stays a text
 * node. A `highlight` that isn't present in the text renders the text unchanged
 * rather than dropping copy.
 */
export function HighlightedText({
  text,
  highlight,
  highlightClassName,
  textClassName,
}: HighlightedTextProps) {
  const at = highlight === '' ? -1 : text.indexOf(highlight)

  if (at === -1) {
    return wrap(text, textClassName)
  }

  const after = at + highlight.length

  return (
    <>
      {at > 0 && wrap(text.slice(0, at), textClassName)}
      <span className={highlightClassName}>{highlight}</span>
      {after < text.length && wrap(text.slice(after), textClassName)}
    </>
  )
}
